import {createEisTypeObject, getKey, getByKeyPath} from 'Quoting/utils/utilities';
import {Map as ImmutableMap} from 'immutable';

export const createNewGIM2PlanObjectWithDefaults = (
  plan: any,
  rootId: string,
  quoteParentId: string,
  coverageEffectiveDate: string,
  defaultValues: ImmutableMap<string, any>
) => {
  const key = getKey(rootId, quoteParentId);
  const parentId = key.id;

  return {
    exclusions: createBenefitPlanExclusions(defaultValues, rootId, parentId),
    planCd: plan.planCode,
    planGroupsLinks: [],
    _type: 'HIBenefitPlan',
    planName: plan.name,
    _key: key,
    coverageDefs: plan.coverages
      .filter((coverage: any) => coverage.applicability === 'Mandatory')
      .map(() => createCoverageDef(defaultValues, rootId, parentId, coverageEffectiveDate)),
    rateAdjustmentLimits: {
      ...createEisTypeObject('HIRateAdjustmentLimits', rootId, parentId),
      maxAdjustmentPct: getByKeyPath(defaultValues, 'HIRateAdjustmentLimits.maxAdjustmentPct'),
      minAdjustmentPct: getByKeyPath(defaultValues, 'HIRateAdjustmentLimits.minAdjustmentPct'),
    },
    offerStatus: 'offered',
  };
};

const createCoverageDef = (
  defaultValues: ImmutableMap<string, any>,
  planRootId: string,
  planParentId: string,
  coverageEffectiveDate: string
) => {
  const key = getKey(planRootId, planParentId);
  const rootId = planRootId;
  const parentId = key.id;

  return {
    _key: key,
    code: defaultValues.get('HICoverageDefinition.code'),
    coverageBasisCd: defaultValues.get('HICoverageDefinition.coverageBasisCd'),
    benefitAmountSpouse: {
      benefitPct: defaultValues.get('HIBenefitAmountSpouse.benefitPct'),
      ...createEisTypeObject('HIBenefitAmountSpouse', rootId, parentId),
    },
    preExLimitationAndPregnancyWaitingPeriodDetails: {
      pregnancyWaitingPeriodMonthsCd: 'None',
      preExistingConditionLimitationPeriodCd: 'None',
      preExLimitationAndPregnancyWaitingPeriodCd: defaultValues.get(
        'HIPreExLimitationAndPregnancyWaitingPeriod.preExLimitationAndPregnancyWaitingPeriodCd'
      ),
      ...createEisTypeObject('HIPreExLimitationAndPregnancyWaitingPeriod', rootId, parentId),
    },
    terminationAgeCd: defaultValues.get('HICoverageDefinition.terminationAgeCd'),
    benefitStructure: createGIM2BenefitStructure(defaultValues, rootId, parentId),
    coveredEvents: defaultValues.get('HICoverageDefinition.coveredEvents'),
    effDate: coverageEffectiveDate,
    eligibilities: createHIEligibilities(defaultValues, rootId, parentId),
    whoCovered: createWhoCovered(rootId, parentId),
    ratingDetails: {
      rateBasisCd: defaultValues.get('HICoverageRating.rateBasisCd'),
      rateFormatCd: defaultValues.get('HICoverageRating.rateFormatCd'),
      ...createEisTypeObject('HICoverageRating', rootId, parentId),
    },
    originalEffDate: coverageEffectiveDate,
    censusTypeCd: defaultValues.get('HICoverageDefinition.censusTypeCd'),
    coverageGroupsLinks: [],
    _type: 'HICoverageDefinition',
    fundingStructure: {
      ...createEisTypeObject('HIFundingStructure', rootId, parentId),
      requiredGroupAccessPct: defaultValues.get('HIFundingStructure.requiredGroupAccessPct'),
      taxabilityCd: defaultValues.get('HIFundingStructure.taxabilityCd'),
      minParticipantsNum: defaultValues.get('HIFundingStructure.minParticipantsNum'),
      contributionTypeCd: defaultValues.get('HIFundingStructure.contributionTypeCd'),
      fundingTypeCd: defaultValues.get('HIFundingStructure.fundingTypeCd'),
      payrollFrequency: undefined,
    },
    benefitSchedule: createEisTypeObject('HIBenefitSchedule', rootId, parentId),
    benefitAmountChild: {
      benefitPct: defaultValues.get('HIBenefitAmountChild.benefitPct'),
      ...createEisTypeObject('HIBenefitAmountChild', rootId, parentId),
    },
    offerStatus: 'offered',
    issueAges: {
      minIssueAgeChild: defaultValues.get('HIEligibilityIssueAges.minIssueAgeChild'),
      maxIssueAgeChild: defaultValues.get('HIEligibilityIssueAges.maxIssueAgeChild'),
      minIssueAgeIndividual: defaultValues.get('HIEligibilityIssueAges.minIssueAgeIndividual'),
      minIssueAgeSpouse: defaultValues.get('HIEligibilityIssueAges.minIssueAgeSpouse'),
      ...createEisTypeObject('HIEligibilityIssueAges', rootId, parentId),
    },
    whoCoveredCd: defaultValues.get('HICoverageDefinition.whoCoveredCd'),
  };
};

const createBenefitPlanExclusions = (defaultValues: ImmutableMap<string, any>, rootId?: string, parentId?: string) =>
  createExclusions(
    [
      {
        exclusionEntityName: 'HIPlanExclMentalOrNervous',
        isExclusion: defaultValues.get('HIPlanExclMentalOrNervous.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclAlcoholism',
        isExclusion: defaultValues.get('HIPlanExclAlcoholism.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclInjOrSickPriorToCoverageEffectiveDate',
        isExclusion: defaultValues.get('HIPlanExclInjOrSickPriorToCoverageEffectiveDate.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclParticipationInAeronautics',
        isExclusion: defaultValues.get('HIPlanExclParticipationInAeronautics.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclNewbornCare',
        isExclusion: defaultValues.get('HIPlanExclNewbornCare.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclDrivingMotorizedVehicle',
        isExclusion: defaultValues.get('HIPlanExclDrivingMotorizedVehicle.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclIntoxicantNarcoticHallucinogenAlcohol',
        isExclusion: defaultValues.get('HIPlanExclIntoxicantNarcoticHallucinogenAlcohol.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclWarOrActOfWar',
        isExclusion: defaultValues.get('HIPlanExclWarOrActOfWar.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclFelonyOrAssaultOrIllegalOccupation',
        isExclusion: defaultValues.get('HIPlanExclFelonyOrAssaultOrIllegalOccupation.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclSuicide',
        isExclusion: defaultValues.get('HIPlanExclSuicide.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclCosmeticDentalOrPlasticSurgery',
        isExclusion: defaultValues.get('HIPlanExclCosmeticDentalOrPlasticSurgery.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclIntentionallySelfInflictedInjuries',
        isExclusion: defaultValues.get('HIPlanExclIntentionallySelfInflictedInjuries.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclConfinementBeforeEffDateOfCov',
        isExclusion: defaultValues.get('HIPlanExclConfinementBeforeEffDateOfCov.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclReversalOfATubalLigationOrVasectomy',
        isExclusion: defaultValues.get('HIPlanExclReversalOfATubalLigationOrVasectomy.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclArtificialInsemination',
        isExclusion: defaultValues.get('HIPlanExclArtificialInsemination.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclTreatmentRenderedByPersonOrFamily',
        isExclusion: defaultValues.get('HIPlanExclTreatmentRenderedByPersonOrFamily.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclTreatmentOutsideOfUnitedStates',
        isExclusion: defaultValues.get('HIPlanExclTreatmentOutsideOfUnitedStates.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclWillfulCriminalActivity',
        isExclusion: defaultValues.get('HIPlanExclWillfulCriminalActivity.isExclusion'),
      },
    ],
    rootId,
    parentId
  );

const createExclusions = (
  exclusions: {exclusionEntityName: string; isExclusion: boolean}[],
  rootId?: string,
  parentId?: string
) =>
  exclusions.map(({exclusionEntityName, isExclusion}) => ({
    ...createEisTypeObject(exclusionEntityName, rootId, parentId),
    isExclusion: isExclusion,
  }));

const createHIEligibilities = (defaultValues: ImmutableMap<string, any>, rootId: string, parentId: string) => {
  return [
    {
      waitingPeriodAmount: defaultValues.get('HIEligibility.waitingPeriodAmount'),
      waitingPeriodModeCd: defaultValues.get('HIEligibility.waitingPeriodModeCd'),
      minHourlyReq: defaultValues.get('HIEligibility.minHourlyReq'),
      waitingPeriodDefCd: defaultValues.get('HIEligibility.waitingPeriodDefCd'),
      eligibilityTypeCd: 'ExistingEmployees',
    },
    {
      waitingPeriodModeCd: defaultValues.get('HIEligibility.waitingPeriodModeCd'),
      minHourlyReq: defaultValues.get('HIEligibility.minHourlyReq'),
      eligibilityTypeCd: 'NewEmployees',
    },
  ].map(e => ({...e, ...createEisTypeObject('HIEligibility', rootId, parentId)}));
};

const createWhoCovered = (rootId: string, parentId: string) =>
  ['Individual', 'IndividualAndSpouse', 'IndividualAndChildren', 'Family'].map(tierCd => ({
    ...createEisTypeObject('HITier', rootId, parentId),
    tierCd: tierCd,
  }));

export const createGIM2BenefitStructure = (defaultValues: ImmutableMap<string, any>, rootId: string, parentId: string) => {
  return {
    _key: getKey(rootId, parentId),
    isICUConfinementApplied: defaultValues.get('HIBenefitStructure.isICUConfinementApplied'),
    isSubstanceAbuseConfinementApplied: false,
    icuConfinementDetails: {
      benefitAmount: defaultValues.get('HIICUConfinementBenefit.benefitAmount'),
      maxBenefitNumber: defaultValues.get('HIICUConfinementBenefit.maxBenefitNumber'),
      ...createEisTypeObject('HIICUConfinementBenefit', rootId, parentId),
    },
    isGroupPolicyFacilityRiderApplied: false,
    icuStepDownAdmissionDetails: createEisTypeObject('HIICUStepDownAdmissionBenefit', rootId, parentId),
    offerStatus: 'offered',
    icuDailyConfinementDetails: createEisTypeObject('HIICUDailyConfinementBenefit', rootId, parentId),
    hosAdmissionDetails: {
      benefitAmount: defaultValues.get('HIHosAdmissionBenefit.benefitAmount'),
      complicationsFromPregnancyCd: defaultValues.get('HIHosAdmissionBenefit.complicationsFromPregnancyCd'),
      firstDayConfinementPaymentFreqLimitCd: defaultValues.get(
        'HIHosAdmissionBenefit.firstDayConfinementPaymentFreqLimitCd'
      ),
      newbornInitialConfinementPct: defaultValues.get('HIHosAdmissionBenefit.newbornInitialConfinementPct'),
      normalPregnancyCd: defaultValues.get('HIHosAdmissionBenefit.normalPregnancyCd'),
      ...createEisTypeObject('HIHosAdmissionBenefit', rootId, parentId),
    },
    hosConfinementDetails: {
      benefitAmount: defaultValues.get('HIHosConfinementBenefit.benefitAmount'),
      maxBenefitNumber: defaultValues.get('HIHosConfinementBenefit.maxBenefitNumber'),
      newbornConfinementPct: defaultValues.get('HIHosConfinementBenefit.newbornConfinementPct'),
      ...createEisTypeObject('HIHosConfinementBenefit', rootId, parentId),
    },
    isHosAdmissionApplied: defaultValues.get('HIBenefitStructure.isHosAdmissionApplied'),
    isHosConfinementApplied: defaultValues.get('HIBenefitStructure.isHosConfinementApplied'),
    isFirstDayHospitalIntensiveCareConfinementBenefitApplied: false,
    isWellnessBenefitApplied: defaultValues.get('HIBenefitStructure.isWellnessBenefitApplied'),
    behavioralHealthServicesBenefitDetails: {
      ...createEisTypeObject('HiBehavioralHealthServicesBenefit', rootId, parentId),
      exclusions: createBehavioralHealthServicesExclusions(defaultValues, rootId, parentId),
    },
    _type: 'HIBenefitStructure',
    isPortabilityApplied: undefined,
    icuDailyStepDownConfinementDetails: createEisTypeObject('HIICUDailyStepDownConfinementBenefit', rootId, parentId),
    isNervousDisorderConfinementApplied: false,
    observationUnitDetails: createEisTypeObject('HIObservationUnit', rootId, parentId),
    portabilityDetails: {
      ...createEisTypeObject('HIPortability', rootId, parentId),
      limitPortedCovDurationInd: defaultValues.get('HIPortability.limitPortedCovDurationInd'),
      covDuration: defaultValues.get('HIPortability.covDuration'),
    },
  };
};

const createBehavioralHealthServicesExclusions = (
  defaultValues: ImmutableMap<string, any>,
  rootId?: string,
  parentId?: string
) =>
  createExclusions(
    [
      {
        exclusionEntityName: 'HiBhsExclExclConfinementBeforeEffDateOfCov',
        isExclusion: defaultValues.get('HiBhsExclExclConfinementBeforeEffDateOfCov.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclWillfulCriminalActivity',
        isExclusion: defaultValues.get('HiBhsExclExclWillfulCriminalActivity.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclTreatmentOutsideOfUnitedStates',
        isExclusion: defaultValues.get('HiBhsExclExclTreatmentOutsideOfUnitedStates.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclCosmeticDentalOrPlasticSurgery',
        isExclusion: defaultValues.get('HiBhsExclExclCosmeticDentalOrPlasticSurgery.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclSuicide',
        isExclusion: defaultValues.get('HiBhsExclExclSuicide.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclIntoxicantNarcoticHallucinogenAlcohol',
        isExclusion: defaultValues.get('HiBhsExclExclIntoxicantNarcoticHallucinogenAlcohol.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclArtificialInsemination',
        isExclusion: defaultValues.get('HiBhsExclExclArtificialInsemination.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclWarOrActOfWar',
        isExclusion: defaultValues.get('HiBhsExclExclWarOrActOfWar.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclDrivingMotorizedVehicle',
        isExclusion: defaultValues.get('HiBhsExclExclDrivingMotorizedVehicle.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclIntentionallySelfInflictedInjuries',
        isExclusion: defaultValues.get('HiBhsExclExclIntentionallySelfInflictedInjuries.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclParticipationInAeronautics',
        isExclusion: defaultValues.get('HiBhsExclExclParticipationInAeronautics.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclFelonyOrAssaultOrIllegalOccupation',
        isExclusion: defaultValues.get('HiBhsExclExclFelonyOrAssaultOrIllegalOccupation.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclTreatmentRenderedByPersonOrFamily',
        isExclusion: defaultValues.get('HiBhsExclExclTreatmentRenderedByPersonOrFamily.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclNewbornCare',
        isExclusion: defaultValues.get('HiBhsExclExclNewbornCare.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclMentalOrNervous',
        isExclusion: defaultValues.get('HiBhsExclExclMentalOrNervous.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclReversalOfATubalLigationOrVasectomy',
        isExclusion: defaultValues.get('HiBhsExclExclReversalOfATubalLigationOrVasectomy.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclInjOrSickPriorToCoverageEffectiveDate',
        isExclusion: defaultValues.get('HiBhsExclExclInjOrSickPriorToCoverageEffectiveDate.isExclusion'),
      },
      {
        exclusionEntityName: 'HiBhsExclExclAlcoholism',
        isExclusion: defaultValues.get('HiBhsExclExclAlcoholism.isExclusion'),
      },
    ],
    rootId,
    parentId
  );
